'use client'

/**
 * A6: an account of another organisation on a company's address (branded sign-in, 3 Nov 2026; contract
 * docs/superpowers/specs/2026-11-03-branded-sign-in-design.md sections 3 and 4.5, decision 6).
 *
 * HumanFirewall is a security-awareness platform. csb.humanfirewall.ai serves CSB Bank's people only: a valid admin of
 * any other company who signs in there is refused BEFORE a session exists (`sign_in_wrong_company_address` at the
 * password route, the hand-off exchange or the provider's callback), and an API call made from the address by another
 * company's account is refused per request (`company_address_wrong_company`). Either way the page says that nothing
 * was opened, and offers the way to the shared sign-in and a way to try another account. The account is named only
 * when the person typed it here; a provider's round trip never puts an address in the URL.
 */

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useTranslation } from '@/lib/i18n'
import { richText } from '@/lib/i18n/rich-text'
import type { SignInCompany } from './sign-in-company-card'

/**
 * HumanFirewall's shared sign-in page for this box (dash.humanfirewall.ai, uaeapp..., staging): the API's answer
 * (`platformUrl` of the sign-in options, from the box's FRONTEND_URL), else what the build knows (NEXT_PUBLIC_APP_URL).
 * Null otherwise: the page then offers only "Use a different account".
 */
export function platformSignInUrl(platformUrl?: string | null): { href: string; host: string } | null {
  const raw = (platformUrl || process.env.NEXT_PUBLIC_APP_URL || '').trim()
  if (!raw) return null
  try {
    const url = new URL(raw)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    return { href: `${url.origin}/login`, host: url.host }
  } catch {
    return null
  }
}

export function SignInWrongCompany({
  company,
  email,
  onDifferentAccount,
  differentAccountHref,
  platformUrl,
}: {
  company: SignInCompany | null
  /** The account that was refused, when the person typed it on this page. */
  email: string | null
  onDifferentAccount?: () => void
  differentAccountHref?: string
  /** The sign-in options' platformUrl (this box's shared sign-in page), when the answer carried one. */
  platformUrl?: string | null
}) {
  const { t } = useTranslation()
  const platform = platformSignInUrl(platformUrl)
  const linkClass = 'block w-full text-center text-xs text-muted-foreground hover:text-primary'
  return (
    <Card data-testid="sign-in-wrong-company">
      <CardHeader className="text-center pb-4">
        <h2 className="text-xl leading-none font-semibold">
          {company
            ? t('signInPage.wrongCompany.title', { company: company.name })
            : t('signInPage.wrongCompany.titleUnknown')}
        </h2>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {email
            ? richText(t('signInPage.wrongCompany.signedInAs'), {
                email: (
                  <b className="font-semibold" dir="ltr">
                    {email}
                  </b>
                ),
              })
            : t('signInPage.wrongCompany.signedInOther')}
        </div>
        {platform ? (
          <Button asChild className="h-11 w-full sm:h-10">
            <a href={platform.href}>
              {/* one flex item: Button is a flex row with a gap, which doubled the space before the host */}
              <span>{richText(t('signInPage.wrongCompany.goTo'), { host: <span dir="ltr">{platform.host}</span> })}</span>
            </a>
          </Button>
        ) : null}
        {differentAccountHref ? (
          <Link href={differentAccountHref} className={linkClass}>
            {t('signInPage.wrongCompany.differentAccount')}
          </Link>
        ) : (
          <button type="button" className={linkClass} onClick={onDifferentAccount}>
            {t('signInPage.wrongCompany.differentAccount')}
          </button>
        )}
      </CardContent>
    </Card>
  )
}
